import {Component, ConfigOptions} from 'panel';

import {getCookie} from 'common/util/cookie';
import {compoundSentence} from 'common/util/string';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {track, TrackEvents} from 'media/js/auth/tracking';

import {FormErrors, FormField, FormValues, getAllValidationErrors, TwoFactorStep} from '../validation';
import template from './index.jade';
import './index.styl';

interface SMSTwoFactorState {
  formValues: FormValues;
  formErrors: FormErrors;
}

export class SMSTwoFactor extends Component<SMSTwoFactorState> {
  private form: HTMLFormElement;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      errors: {type: `json`, description: `list of errors from API, shown in banner`},
      step: {type: `number`, description: `stage of verification process. 0 for setup, 1 for verification.`},
      phone: {type: `string`},
    };
  }

  get config(): ConfigOptions<SMSTwoFactorState> {
    return {
      template,
      defaultState: {
        formValues: {
          [FormField.PhoneNumber]: ``,
          [FormField.CountryCode]: `+1`,
          [FormField.VerificationCode]: ``,
        },
        formErrors: {
          [FormField.PhoneNumber]: null,
          [FormField.CountryCode]: null,
          [FormField.VerificationCode]: null,
        },
      },
      helpers: {
        TwoFactorStep,
        FormField,
        onFormInserted: ({elm}) => {
          this.form = elm;
        },

        handleInput: (field: FormField, ev) => {
          this.update({formValues: {...this.state.formValues, [field]: ev.target.value}});
        },

        handleBlur: () => {
          this.validate();
        },

        handleSubmit: (ev) => {
          this.validate() || ev.preventDefault();
        },

        handleSubmitClick: () => {
          this.validate() && this.form.submit();
        },

        getCsrfToken: () => {
          return getCookie(`csrftoken`);
        },

        getErrors: () => {
          const errors = this.getJSONAttribute(`errors`);
          if (errors && errors.length) {
            return compoundSentence(errors);
          }
          return ``;
        },

        getInfo: () => {
          if (this.attr(`step`) === TwoFactorStep.Verify && this.attr(`phone`)) {
            return `7 digit code sent to a number ending with ${this.attr(`phone`)}.`;
          }
          return ``;
        },

        handleClickBackButton: (): void => {
          if (this.attr(`step`) === TwoFactorStep.Verify) {
            window.history.back();
          } else {
            window.location.assign(`/security/twofactor/setup`);
          }
        },
      },
    };
  }

  private validate() {
    const formErrors = getAllValidationErrors(this.state.formValues, this.attr(`step`));
    this.update({formErrors});
    return !Object.values(formErrors).some(Boolean);
  }

  connectedCallback(): void {
    super.connectedCallback();

    switch (this.attr(`step`)) {
      case TwoFactorStep.Setup:
        track(TrackEvents.ViewSMSSetupPage);
        break;
      case TwoFactorStep.Verify:
        track(TrackEvents.ViewSMSVerificationPage);
        break;
      default:
        break;
    }
  }
}

export const SMSTwoFactorEl = defineAndMakeJsxFactory(`mp-two-factor-sms`, SMSTwoFactor);
